import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, Map as MapIcon, Building2, MapPin, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { useSite } from "../../contexts/SiteContext";
import { SPORTS, AGE_GROUPS, sportById } from "../../lib/sports";
import SiteLayout from "../../components/site/SiteLayout";
import { CoachCard, Eyebrow } from "../../components/site/ui";

const chip = (a) => `shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${a ? "bg-[#0A1628] text-white border-[#0A1628]" : "bg-white text-[#0A1628] border-slate-200 hover:border-[#0A1628]"}`;
const sel = "rounded-full border border-slate-200 px-3 py-2 bg-white text-sm text-[#0A1628]";

const Filters = ({ f, set, fixedSport }) => {
  const { L, lang, cur, sportLabel, enabledSports } = useSite();
  const sports = SPORTS.filter((s) => !enabledSports.length || enabledSports.includes(s.id));
  return (
    <div className="space-y-4" data-testid="search-filters">
      {!fixedSport && (
        <div className="flex gap-2 overflow-x-auto otp-noscroll" data-testid="sport-filter-chips">
          <button type="button" className={chip(!f.sport)} onClick={() => set({ sport: "" })} data-testid="sport-chip-all">{L("All sports", "Всички")}</button>
          {sports.map((s) => <button type="button" key={s.id} className={chip(f.sport === s.id)} onClick={() => set({ sport: s.id })} data-testid={`sport-chip-${s.id}`}>{sportLabel(s.id)}</button>)}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-sm">
        <span className="inline-flex items-center gap-2 font-semibold text-[#0A1628]"><SlidersHorizontal className="w-4 h-4" /> {L("Filters", "Филтри")}</span>
        <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 pl-3 pr-1 py-1 bg-white">
          <MapPin className="w-4 h-4 text-[#1D4ED8]" />
          <input value={f.location} onChange={(e) => set({ location: e.target.value })} placeholder={L("City or ZIP", "Град / пощ. код")} className="w-32 outline-none text-[#0A1628] bg-transparent" data-testid="filter-location-input" />
        </label>
        <select value={f.distance} onChange={(e) => set({ distance: e.target.value })} className={sel} data-testid="filter-distance">
          <option value="">{L("Any distance", "Всяко разстояние")}</option>
          {[5, 10, 25, 50].map((m) => <option key={m} value={m}>{L(`Within ${m} mi`, `До ${Math.round(m * 1.6)} км`)}</option>)}
        </select>
        <select value={f.max_price} onChange={(e) => set({ max_price: e.target.value })} className={sel} data-testid="filter-max-price">
          <option value="">{L("Any price", "Всяка цена")}</option>
          {[50, 75, 100, 150, 200].map((p) => <option key={p} value={p}>{`${L("Up to", "До")} ${cur === "EUR" ? "€" : "$"}${p}/${L("h", "ч")}`}</option>)}
        </select>
        <select value={f.session_type} onChange={(e) => set({ session_type: e.target.value })} className={sel} data-testid="filter-session-type">
          <option value="">{L("Private or group", "Индивид. или група")}</option>
          <option value="group">{L("Group sessions", "Групови")}</option>
        </select>
        <select value={f.age_group} onChange={(e) => set({ age_group: e.target.value })} className={sel} data-testid="filter-age-group">
          <option value="">{L("All ages", "Всички възрасти")}</option>
          {AGE_GROUPS.map((a) => <option key={a.id} value={a.id}>{lang === "bg" ? a.bg : a.en}</option>)}
        </select>
        <select value={f.min_rating} onChange={(e) => set({ min_rating: e.target.value })} className={sel} data-testid="filter-rating">
          <option value="">{L("Any rating", "Всеки рейтинг")}</option>
          <option value="4">{"4.0+"}</option><option value="4.5">{"4.5+"}</option>
        </select>
        <input value={f.specialization} onChange={(e) => set({ specialization: e.target.value })} placeholder={L("Specialization", "Специализация")} className={`${sel} w-36`} data-testid="filter-specialization" />
        <label className="inline-flex items-center gap-2 cursor-pointer text-[#0A1628]">
          <input type="checkbox" checked={!!f.available} onChange={(e) => set({ available: e.target.checked ? "14" : "" })} className="w-4 h-4 accent-[#1D4ED8]" data-testid="filter-available" />
          {L("Has open slots", "Със свободни часове")}
        </label>
        <select value={f.sort} onChange={(e) => set({ sort: e.target.value })} className={sel} data-testid="sort-select">
          <option value="recommended">{L("Recommended", "Препоръчани")}</option>
          <option value="rating">{L("Top rated", "Най-висок рейтинг")}</option>
          <option value="price">{L("Lowest price", "Най-ниска цена")}</option>
          <option value="distance">{L("Nearest", "Най-близо")}</option>
        </select>
      </div>
    </div>
  );
};

const Academies = ({ location }) => {
  const { L } = useSite();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/public/academies", { params: { city: location || undefined } }).then(({ data }) => setList(Array.isArray(data) ? data : data.academies || [])).catch(() => setList([])); }, [location]);
  if (!list) return <Loader2 className="w-6 h-6 animate-spin text-[#1D4ED8] mt-10" />;
  if (!list.length) return <p className="mt-10 text-slate-500" data-testid="academies-empty">{L("No academies here yet.", "Все още няма академии тук.")}</p>;
  return (
    <div className="mt-6 grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="academies-grid">
      {list.map((a) => (
        <Link key={a.id} to={`/academy/${a.id}`} className="otp-card rounded-3xl border border-slate-200 p-6 flex gap-4 items-center" data-testid={`academy-card-${a.id}`}>
          {a.academy_logo ? <img src={a.academy_logo} alt="" className="w-16 h-16 rounded-2xl object-cover" /> : <span className="w-16 h-16 rounded-2xl bg-[#0A1628] text-white grid place-items-center"><Building2 className="w-7 h-7" /></span>}
          <div className="min-w-0"><p className="font-semibold text-[#0A1628] truncate">{a.academy_name || a.name}</p><p className="text-sm text-slate-500 truncate">{a.city}</p></div>
        </Link>
      ))}
    </div>
  );
};

export default function Search() {
  const { sport: fixedSport } = useParams();
  const [params, setParams] = useSearchParams();
  const { L, cur, sportLabel } = useSite();
  const f = useMemo(() => ({
    sport: fixedSport || params.get("sport") || "", location: params.get("location") ?? "", distance: params.get("distance") || "",
    max_price: params.get("max_price") || "", session_type: params.get("session_type") || "", age_group: params.get("age_group") || "",
    min_rating: params.get("min_rating") || "", specialization: params.get("specialization") || "", available: params.get("when") || params.get("available") || "",
    sort: params.get("sort") || "recommended", tab: params.get("tab") || "coaches",
  }), [params, fixedSport]);
  const set = (v) => { const n = new URLSearchParams(params); Object.entries(v).forEach(([k, val]) => (val ? n.set(k, val) : n.delete(k))); if ("available" in v) n.delete("when"); setParams(n, { replace: true }); };
  const [data, setData] = useState(null);
  useEffect(() => {
    if (f.tab !== "coaches") return;
    const t = setTimeout(() => {
      setData(null);
      api.get("/search/coaches", { params: {
        sport: f.sport || undefined, location: f.location || undefined, distance_km: f.distance ? Math.round(f.distance * 1.609) : undefined,
        max_price: f.max_price || undefined, session_type: f.session_type || undefined, age_group: f.age_group || undefined,
        min_rating: f.min_rating || undefined, specialization: f.specialization || undefined, available_days: f.available || undefined,
        sort: f.sort, currency: cur,
      } }).then(({ data }) => setData(data)).catch(() => setData({ coaches: [], total: 0 }));
    }, 300);
    return () => clearTimeout(t);
  }, [f, cur]);
  const sp = sportById(f.sport);
  return (
    <SiteLayout>
      {fixedSport && sp && (
        <section className="px-2 sm:px-4 pt-2 sm:pt-4" data-testid="sport-hero">
          <div className="relative rounded-[28px] overflow-hidden bg-[#0A1628] min-h-[300px] sm:min-h-[380px] flex items-end">
            <img src={sp.img} alt={sp.en} className="absolute inset-0 w-full h-full object-cover otp-kenburns" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/50 to-transparent" />
            <div className="relative px-6 sm:px-10 pb-10 max-w-[1400px] w-full mx-auto">
              <Eyebrow light>{sp.tag}</Eyebrow>
              <h1 className="otp-display uppercase text-white text-5xl sm:text-7xl mt-4" data-testid="sport-title">{sportLabel(sp.id)}</h1>
              <p className="text-white/75 mt-3 max-w-xl">{L(`Private and group ${sp.en.toLowerCase()} coaching. Book verified coaches near you.`, `Индивидуални и групови тренировки по ${sp.bg.toLowerCase()}.`)}</p>
            </div>
          </div>
        </section>
      )}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 pt-6" data-testid="search-page">
        <div className="flex gap-2 mb-4">
          <button type="button" className={chip(f.tab === "coaches")} onClick={() => set({ tab: "" })} data-testid="tab-coaches">{L("Coaches", "Треньори")}</button>
          <button type="button" className={chip(f.tab === "academies")} onClick={() => set({ tab: "academies" })} data-testid="tab-academies">{L("Academies", "Академии")}</button>
          <Link to="/coaches-map" className={`${chip(false)} inline-flex items-center gap-1.5 ml-auto`} data-testid="map-view-link"><MapIcon className="w-4 h-4" /> {L("Map", "Карта")}</Link>
        </div>
        {f.tab === "academies" ? <Academies location={f.location} /> : (
          <>
            <div className="sticky top-16 lg:top-[72px] z-30 bg-white/90 backdrop-blur-xl py-4 -mx-4 px-4 sm:-mx-8 sm:px-8 border-b border-slate-100"><Filters f={f} set={set} fixedSport={fixedSport} /></div>
            <h2 className="otp-display uppercase text-3xl sm:text-4xl mt-6 text-[#0A1628]" data-testid="search-results-title">
              {data ? data.total : "…"} {f.sport ? `${sportLabel(f.sport)} ` : ""}{L("coaches", "треньори")}{f.location && <span className="text-[#1D4ED8]"> · {f.location}</span>}
            </h2>
            <p className="text-slate-500 mt-1 text-sm">{L("Prices are set by each coach. Converted prices (≈) are estimates — you pay in the coach's currency.", "Цените се определят от треньора. Конвертираните (≈) са ориентировъчни — плащате във валутата на треньора.")}</p>
            {!data ? <Loader2 className="w-6 h-6 animate-spin text-[#1D4ED8] mt-10" /> : data.coaches.length ? (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-8" data-testid="search-results-grid">{data.coaches.map((c) => <CoachCard key={c.id} c={c} />)}</div>
            ) : (
              <div className="mt-10 rounded-3xl border border-dashed border-slate-300 p-10 text-center" data-testid="search-empty-state">
                <p className="otp-display uppercase text-2xl text-[#0A1628]">{data.location_found === false ? L("Location not found", "Локацията не е намерена") : L("No coaches match yet", "Все още няма съвпадения")}</p>
                <p className="text-slate-500 mt-2">{L("Try widening the distance or removing a filter.", "Увеличи разстоянието или махни филтър.")}</p>
                <Link to="/register?role=coach" className="inline-block mt-5 font-semibold text-[#1D4ED8]">{L("Are you a coach? Join for free →", "Треньор ли сте? Присъединете се безплатно →")}</Link>
              </div>
            )}
          </>
        )}
      </div>
    </SiteLayout>
  );
}
